import React, { useState, useRef, useCallback, useMemo, useEffect, memo } from 'react';
import debounce from 'lodash/debounce';
import filter from 'lodash/filter';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import { GoogleMap, Marker as MarkerLib, MarkerClusterer } from '@react-google-maps/api';
import { Minus, Plus, FullscreenEnter, FullscreenExit, MapMyLocation } from '@yme/assets/icons';
import { BottomSheet } from '@yme/atoms/bottom-sheet';
import { FloatingButton } from '@yme/atoms/button-floating';
import { Toast } from '@yme/atoms/toast';
import { usePrevious } from '@yme/hooks/usePrevious';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { useTranslation } from '@yme/services/i18n';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { INDENT } from '@yme/tokens/sizes';
import { keyCodes } from '@yme/utils/constants';
import { withGoogleApi } from '@yme/utils/withGoogleApi';
import { BottomControlContainerStyled, mapNodeStyle, ToastWrapperStyled, TopControlContainerStyled, WrapperStyled, } from './MapStyled';
import { Marker } from './Marker';
import { INITIAL_CENTER, INITIAL_ZOOM, PRESELECTED_MARKER_ZOOM, clusterOptions, infoWindowDefaultOptions, } from './constants';
import { areMarkersChanged, fitMapToMarkers, getMapOptions } from './helpers';
const MapComponent = memo(({ markers, selectedMarkerId, preSelectedMarkerId, myLocation, InfoWindow, infoWindowOptions = infoWindowDefaultOptions, mapOptions: incomingMapOptions, fitToMarkers = true, isHiddenCloseIcon = false, getMobileInfoWindow, onMarkerSelect, onMarkerDismiss, onBoundsChanged, onZoomChanged, onMapLoaded, }) => {
    const { t } = useTranslation();
    const wrapperRef = useRef(null);
    const [mapInstance, setMapInstance] = useState(null);
    const [center, setCenter] = useState(INITIAL_CENTER);
    const [zoom, setZoom] = useState(INITIAL_ZOOM);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [activeMarkerId, setActiveMarkerId] = useState(null);
    const [isMobileInfoWindowOpened, setIsMobileInfoWindowOpened] = useState(false);
    const [isInfoWindowOpened, setIsInfoWindowOpened] = useState(false);
    const { isInfiniteDesktopView: isDesktop } = useWindowSize();
    const prevMarkers = usePrevious(markers);
    const toggleFullscreen = useCallback(() => {
        setIsFullscreen(!isFullscreen);
    }, [isFullscreen]);
    const zoomIn = useCallback(() => {
        setZoom(zoom + 1);
    }, [zoom]);
    const zoomOut = useCallback(() => {
        setZoom(zoom - 1);
    }, [zoom]);
    const handleMapLoaded = useCallback((instance) => {
        setMapInstance(instance);
        onMapLoaded?.(instance);
    }, [onMapLoaded]);
    const handleZoomChanged = useCallback(() => {
        if (mapInstance) {
            const zoom = mapInstance.getZoom();
            setZoom(zoom);
            onZoomChanged?.(zoom);
        }
    }, [mapInstance, onZoomChanged]);
    const handleDragEnd = useCallback(() => {
        if (mapInstance) {
            const { lat, lng } = mapInstance.getCenter();
            setCenter({
                lat: lat(),
                lng: lng(),
            });
        }
    }, [mapInstance]);
    const handleBoundsChanged = useMemo(() => debounce(() => {
        if (onBoundsChanged && mapInstance) {
            const bounds = mapInstance.getBounds();
            const markersInBounds = filter(markers, ({ latitude: lat, longitude: lng }) => bounds?.contains({
                lat,
                lng,
            }));
            onBoundsChanged(markersInBounds);
        }
    }, 500), [mapInstance, markers, onBoundsChanged]);
    const handleMarkerClick = useCallback((event) => {
        const { latLng } = event;
        const markerId = find(markers, ({ latitude, longitude }) => latitude === latLng?.lat() && longitude === latLng?.lng())?.vicinityId;
        setActiveMarkerId(markerId);
        onMarkerSelect?.(markerId);
        setIsMobileInfoWindowOpened(true);
        setIsInfoWindowOpened(true);
    }, [markers, onMarkerSelect]);
    const handleMobileBottomSheetDismiss = useCallback(() => {
        setIsMobileInfoWindowOpened(false);
        onMarkerDismiss?.();
    }, [onMarkerDismiss]);
    /**
     * Once map loaded fit it to the area:
     * 1. to the preselected marker id if any
     * 2. to the polygon around all the markers (or when number of markers has been changed)
     */
    useEffect(() => {
        if (!mapInstance || isEmpty(markers)) {
            return;
        }
        // fit map to the specific preselected marker
        if (preSelectedMarkerId) {
            const preselectedMarker = markers.find((marker) => marker.vicinityId === preSelectedMarkerId);
            if (preselectedMarker) {
                const center = new google.maps.LatLng(preselectedMarker.latitude, preselectedMarker.longitude);
                mapInstance.moveCamera({
                    center,
                    zoom: PRESELECTED_MARKER_ZOOM,
                });
            }
        }
        else if (fitToMarkers) {
            // fit map to a polygon with all the markers
            if (areMarkersChanged(markers, prevMarkers)) {
                fitMapToMarkers(mapInstance, markers);
            }
        }
    }, [mapInstance, markers, prevMarkers, preSelectedMarkerId, fitToMarkers]);
    // Effect covers case when some internal prop in all the markers have been changed, for example distance were added to all the markers.
    // In this case we don't want to fit to markers bounds again,
    // But there is need to notify outside via imitation of onBoundsChanged callback that
    // dealers have got a new data, since onBoundsChanged handler might be the part of the filtering flow
    useEffect(() => {
        handleBoundsChanged();
    }, [handleBoundsChanged]);
    // dismiss fullscreen via pressing ESC
    useEffect(() => {
        const dismissFullscreen = (event) => {
            if (event.code === keyCodes.ESCAPE) {
                setIsFullscreen(false);
            }
        };
        if (isFullscreen) {
            document.addEventListener('keydown', dismissFullscreen);
        }
        return () => {
            document.removeEventListener('keydown', dismissFullscreen);
        };
    }, [isFullscreen]);
    // sync map state once selectedMarkerId has been changed from outside
    // todo: md: probably there is need to split to 2 effects (setActiveMarkerId / setIsMobileInfoWindowOpened)
    useEffect(() => {
        setActiveMarkerId(selectedMarkerId);
        if (!isDesktop) {
            setIsMobileInfoWindowOpened(Boolean(selectedMarkerId));
        }
    }, [isDesktop, selectedMarkerId]);
    const renderedMarkers = useMemo(() => (React.createElement(MarkerClusterer, { options: clusterOptions }, (clusterer) => (React.createElement(React.Fragment, null, map(markers, (marker) => {
        const { vicinityId } = marker;
        return (React.createElement(Marker, { key: vicinityId, ...marker, InfoWindow: InfoWindow, infoWindowOptions: infoWindowOptions, isActive: vicinityId === activeMarkerId, clusterer: clusterer, handleCloseInfoWindow: () => setIsInfoWindowOpened(false), isInfoWindowOpened: isInfoWindowOpened, onClick: handleMarkerClick }));
    }))))), [
        InfoWindow,
        activeMarkerId,
        handleMarkerClick,
        infoWindowOptions,
        isInfoWindowOpened,
        markers,
    ]);
    const mapOptions = useMemo(() => getMapOptions(incomingMapOptions, isFullscreen), [incomingMapOptions, isFullscreen]);
    const fullscreenIcon = isFullscreen ? FullscreenExit : FullscreenEnter;
    return (React.createElement(WrapperStyled, { ref: wrapperRef, isHiddenCloseIcon: isHiddenCloseIcon, isFullscreen: isFullscreen },
        React.createElement(GoogleMap, { options: mapOptions, mapContainerStyle: mapNodeStyle, center: center, zoom: zoom, onLoad: handleMapLoaded, onZoomChanged: handleZoomChanged, onDragEnd: handleDragEnd, onBoundsChanged: handleBoundsChanged },
            renderedMarkers,
            myLocation && (React.createElement(MarkerLib, { position: myLocation, icon: MapMyLocation }))),
        React.createElement(TopControlContainerStyled, null,
            React.createElement(FloatingButton, { icon: fullscreenIcon, onClick: toggleFullscreen })),
        React.createElement(BottomControlContainerStyled, null,
            React.createElement(FlexWrapper, { flexDirection: "column" },
                React.createElement(FlexWrapper, { mb: INDENT.xxs },
                    React.createElement(FloatingButton, { icon: Plus, onClick: zoomIn })),
                React.createElement(FloatingButton, { icon: Minus, onClick: zoomOut }))),
        isDesktop && isFullscreen && (React.createElement(ToastWrapperStyled, null,
            React.createElement(Toast, { message: t('googleMap_dismissFullScreen_toast') }))),
        !isDesktop && (React.createElement(BottomSheet, { open: isMobileInfoWindowOpened, onDismiss: handleMobileBottomSheetDismiss },
            InfoWindow,
            getMobileInfoWindow && getMobileInfoWindow(handleMobileBottomSheetDismiss)))));
});
MapComponent.displayName = 'Map_Component';
export const Map = withGoogleApi(MapComponent);
